import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2, AlertTriangle, RefreshCw, CheckCircle2, XCircle, Hourglass,
  Percent, Lock, Filter, Save,
} from 'lucide-react';
import { getAdminToken, setAdminToken } from '../lib/affiliate';

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND}/api`;

const statusBadge = (s) => {
  const map = {
    pending:  { icon: Hourglass,    cls: 'bg-amber-500/10 text-amber-300 border-amber-500/30' },
    approved: { icon: CheckCircle2, cls: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' },
    rejected: { icon: XCircle,      cls: 'bg-red-500/10 text-red-300 border-red-500/30' },
  };
  const c = map[s] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${c.cls}`}>
      <c.icon className="w-3 h-3" /> {s || 'pending'}
    </span>
  );
};

const AdminAffiliatesPage = () => {
  const [token, setToken] = useState(getAdminToken());
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [commissions, setCommissions] = useState({});

  const adminFetch = async (path, options = {}) => {
    const url = new URL(`${API}${path}`);
    if (!url.searchParams.has('token')) url.searchParams.set('token', token);
    const res = await fetch(url.toString(), {
      ...options,
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    });
    let data = null;
    try { data = await res.json(); } catch { data = null; }
    if (!res.ok) {
      const detail = (data && (data.detail || data.message)) || `Request failed (${res.status})`;
      const e = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      e.status = res.status;
      throw e;
    }
    return data;
  };

  const load = async () => {
    setBusy(true); setErr('');
    try {
      const data = await adminFetch('/admin/affiliates');
      setList(data.affiliates || []);
      const cmap = {};
      (data.affiliates || []).forEach((a) => { cmap[a.id] = a.commission_pct; });
      setCommissions(cmap);
    } catch (e) {
      if (e.status === 401) {
        setAuthed(false);
        setErr('Invalid admin token.');
      } else {
        setErr(e.message || 'Failed to load affiliates.');
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (authed) load(); /* eslint-disable-next-line */ }, [authed]);

  const onSubmitToken = (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setAdminToken(token.trim());
    setAuthed(true);
  };

  const onAction = async (id, action) => {
    setBusyId(`${id}:${action}`); setErr('');
    try {
      const path = action === 'commission'
        ? `/admin/affiliates/${id}/commission`
        : `/admin/affiliates/${id}/${action}`;
      const body = action === 'commission'
        ? { commission_pct: Number(commissions[id] || 0) }
        : undefined;
      await adminFetch(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
      await load();
    } catch (e) {
      setErr(e.message || 'Action failed.');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(
    () => filter === 'all' ? list : list.filter((a) => (a.status || '').toLowerCase() === filter),
    [list, filter],
  );

  if (!authed) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center px-4">
        <form onSubmit={onSubmitToken} className="w-full max-w-md p-7 rounded-2xl bg-neutral-900/60 border border-neutral-800 space-y-5" data-testid="admin-login">
          <div className="flex items-center gap-2 text-axon-500"><Lock className="w-5 h-5" /><span className="font-semibold uppercase tracking-wider text-sm">Admin access</span></div>
          <h1 className="font-display text-2xl font-bold">Affiliate moderation</h1>
          <p className="text-sm text-neutral-400">Enter the ADMIN_TOKEN to manage affiliate applications.</p>
          <input
            type="password" value={token} onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            className="w-full bg-neutral-950 px-4 py-3 rounded-xl border border-neutral-700 focus:border-axon-500 outline-none"
            data-testid="admin-token-input"
          />
          {err && <p className="text-sm text-red-400">{err}</p>}
          <button type="submit" className="w-full px-5 py-3 rounded-xl bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white font-bold uppercase tracking-wider text-sm" data-testid="admin-token-submit">
            Continue
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <header className="border-b border-neutral-800 bg-neutral-950/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-axon-500 font-semibold">Admin</p>
            <h1 className="font-display text-xl font-bold">Affiliate applications</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:inline-flex items-center gap-1 text-xs text-neutral-500"><Filter className="w-3.5 h-3.5" /> Status</div>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-neutral-900 border border-neutral-700 rounded-xl px-3 py-2 text-sm" data-testid="admin-filter">
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <button onClick={load} disabled={busy} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-neutral-700 hover:border-axon-500 text-sm font-semibold transition-colors" data-testid="admin-refresh">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </button>
            <button onClick={() => { setAuthed(false); setAdminToken(''); setToken(''); }} className="px-3 py-2 rounded-xl border border-neutral-700 hover:border-red-500 text-sm font-semibold">Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {err && (
          <div className="mb-5 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" /><span>{err}</span>
          </div>
        )}

        {filtered.length === 0 ? (
          <p className="text-neutral-400">No affiliates {filter !== 'all' ? `in ${filter}` : ''}.</p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-neutral-800">
            <table className="w-full text-sm" data-testid="admin-affiliate-table">
              <thead className="bg-neutral-900/70">
                <tr className="text-left text-xs uppercase tracking-wider text-neutral-400">
                  <th className="px-4 py-3">Affiliate</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Subdomain</th>
                  <th className="px-4 py-3">Traffic</th>
                  <th className="px-4 py-3">Stats</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Commission</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} className="border-t border-neutral-800 align-top">
                    <td className="px-4 py-3">
                      <div className="font-semibold">{a.first_name} {a.last_name}</div>
                      <div className="text-xs text-neutral-500 font-mono">{a.descriptor}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{a.email}</div>
                      <div className="text-xs text-neutral-500">{a.phone || '—'} · {a.country}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{a.subdomain}</td>
                    <td className="px-4 py-3">
                      <div>{a.traffic_source}</div>
                      <div className="text-xs text-neutral-500">{a.monthly_traffic || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{(a.stats?.clicks ?? 0)} clicks</div>
                      <div>{(a.stats?.conversions ?? 0)} conv.</div>
                      <div className="font-semibold">${Number(a.stats?.revenue ?? 0).toFixed(2)}</div>
                    </td>
                    <td className="px-4 py-3">{statusBadge(a.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <input
                            type="number" min={0} max={100} step={0.5}
                            value={commissions[a.id] ?? a.commission_pct}
                            onChange={(e) => setCommissions((m) => ({ ...m, [a.id]: e.target.value }))}
                            className="w-20 bg-neutral-950 border border-neutral-700 rounded-lg pl-2 pr-6 py-1.5 text-sm"
                            data-testid={`commission-input-${a.id}`}
                          />
                          <Percent className="w-3 h-3 text-neutral-500 absolute right-2 top-1/2 -translate-y-1/2" />
                        </div>
                        <button
                          onClick={() => onAction(a.id, 'commission')}
                          disabled={busyId === `${a.id}:commission`}
                          className="p-1.5 rounded-lg border border-neutral-700 hover:border-axon-500 transition-colors"
                          title="Save commission"
                          data-testid={`save-commission-${a.id}`}
                        >
                          {busyId === `${a.id}:commission` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => onAction(a.id, 'approve')}
                        disabled={a.status === 'approved' || busyId === `${a.id}:approve`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-xs font-semibold uppercase tracking-wider disabled:opacity-50 transition-colors"
                        data-testid={`approve-${a.id}`}
                      >
                        {busyId === `${a.id}:approve` ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Approve
                      </button>
                      <button
                        onClick={() => onAction(a.id, 'reject')}
                        disabled={a.status === 'rejected' || busyId === `${a.id}:reject`}
                        className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 text-xs font-semibold uppercase tracking-wider disabled:opacity-50 transition-colors"
                        data-testid={`reject-${a.id}`}
                      >
                        {busyId === `${a.id}:reject` ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminAffiliatesPage;
